// Shared UI components.
import React, { useEffect, useMemo, useState } from 'react';
import { toAus, statusLabel, STATUS_COLOURS, DOW, dowOf } from './util';

export function Spinner({ label = 'Loading…' }: { label?: string }) {
  return <div className="spinner-wrap"><div className="spinner" /><span>{label}</span></div>;
}

export function EmptyState({ message, hint }: { message: string; hint?: string }) {
  return (
    <div className="empty-state">
      <div className="empty-icon">▦</div>
      <div>{message}</div>
      {hint && <div className="empty-hint">{hint}</div>}
    </div>
  );
}

export function ErrorBox({ error, onDismiss }: { error: string | string[] | null; onDismiss?: () => void }) {
  if (!error || (Array.isArray(error) && !error.length)) return null;
  const list = Array.isArray(error) ? error : [error];
  return (
    <div className="error-box" role="alert">
      {list.map((e, i) => <div key={i}>{e}</div>)}
      {onDismiss && <button className="btn btn-ghost btn-sm" onClick={onDismiss}>Dismiss</button>}
    </div>
  );
}

export function StatusChip({ status }: { status: string }) {
  const colour = STATUS_COLOURS[status] || '#8a93a6';
  return <span className="chip" style={{ background: colour }}>{statusLabel(status)}</span>;
}

export function Modal({ title, onClose, children, wide }: {
  title: string; onClose: () => void; children: React.ReactNode; wide?: boolean;
}) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);
  return (
    <div className="modal-backdrop" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={'modal' + (wide ? ' modal-wide' : '')} role="dialog" aria-label={title}>
        <div className="modal-head">
          <h3>{title}</h3>
          <button className="btn btn-ghost" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}

export function Confirm({ message, onYes, onNo }: { message: string; onYes: () => void; onNo: () => void }) {
  return (
    <Modal title="Please confirm" onClose={onNo}>
      <p style={{ whiteSpace: 'pre-wrap' }}>{message}</p>
      <div className="btn-row">
        <button className="btn btn-danger" onClick={onYes}>Yes, continue</button>
        <button className="btn" onClick={onNo}>Cancel</button>
      </div>
    </Modal>
  );
}

// Date field with a real calendar picker (native date input), plus a day-of-week
// confirmation underneath — useful when setting roster swings, and it removes any
// ambiguity about whether a date was read as day/month or month/day.
export function DateField({ value, onChange, required, disabled, hideHint }: {
  value: string | null | undefined;
  onChange: (iso: string | null) => void;
  required?: boolean; disabled?: boolean; hideHint?: boolean;
}) {
  const iso = value || '';
  return (
    <span className="date-field">
      <input
        className="input"
        type="date"
        value={iso}
        required={required}
        disabled={disabled}
        onChange={e => onChange(e.target.value || null)}
      />
      {!hideHint && (
        <span className="field-hint date-hint">
          {iso ? `${DOW[dowOf(iso)]} ${toAus(iso)}` : 'Pick a date'}
        </span>
      )}
    </span>
  );
}

export function Field({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
      {hint && <span className="field-hint">{hint}</span>}
    </label>
  );
}

export function Tabs({ tabs, active, onSelect }: {
  tabs: { key: string; label: string }[]; active: string; onSelect: (k: string) => void;
}) {
  return (
    <div className="tabs" role="tablist">
      {tabs.map(t => (
        <button key={t.key} role="tab" aria-selected={active === t.key}
          className={'tab' + (active === t.key ? ' tab-active' : '')}
          onClick={() => onSelect(t.key)}>{t.label}</button>
      ))}
    </div>
  );
}

export type Column<T> = {
  key: string;
  label: string;
  render?: (row: T) => React.ReactNode;
  sortValue?: (row: T) => any;
};

export function DataTable<T extends { id?: number }>({ columns, rows, onRowClick, searchable = true, footer, selectable, selected, onSelect }: {
  columns: Column<T>[];
  rows: T[];
  onRowClick?: (row: T) => void;
  searchable?: boolean;
  footer?: React.ReactNode;
  selectable?: boolean;
  selected?: Set<number>;
  onSelect?: (ids: Set<number>) => void;
}) {
  const [q, setQ] = useState('');
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState(1);

  const filtered = useMemo(() => {
    let out = rows;
    if (q.trim()) {
      const needle = q.toLowerCase();
      out = rows.filter(r => columns.some(c => {
        const v = c.sortValue ? c.sortValue(r) : (r as any)[c.key];
        return v !== null && v !== undefined && String(v).toLowerCase().includes(needle);
      }));
    }
    if (sortKey) {
      const col = columns.find(c => c.key === sortKey);
      out = [...out].sort((a, b) => {
        const va = col?.sortValue ? col.sortValue(a) : (a as any)[sortKey];
        const vb = col?.sortValue ? col.sortValue(b) : (b as any)[sortKey];
        if (va === vb) return 0;
        if (va === null || va === undefined) return 1;
        if (vb === null || vb === undefined) return -1;
        return (va < vb ? -1 : 1) * sortDir;
      });
    }
    return out;
  }, [rows, q, sortKey, sortDir, columns]);

  const allSelected = selectable && filtered.length > 0 && filtered.every(r => selected?.has(r.id!));

  return (
    <div className="table-wrap">
      {searchable && (
        <div className="table-tools">
          <input className="input input-search" placeholder="Search…" value={q} onChange={e => setQ(e.target.value)} />
          <span className="muted">{filtered.length} of {rows.length}</span>
        </div>
      )}
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              {selectable && (
                <th className="th-check">
                  <input type="checkbox" checked={!!allSelected}
                    onChange={e => {
                      const next = new Set(selected);
                      filtered.forEach(r => e.target.checked ? next.add(r.id!) : next.delete(r.id!));
                      onSelect?.(next);
                    }} />
                </th>
              )}
              {columns.map(c => (
                <th key={c.key} onClick={() => {
                  if (sortKey === c.key) setSortDir(-sortDir);
                  else { setSortKey(c.key); setSortDir(1); }
                }}>
                  {c.label}{sortKey === c.key ? (sortDir === 1 ? ' ▲' : ' ▼') : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={columns.length + (selectable ? 1 : 0)}>
                <EmptyState message="Nothing to show." hint={q ? 'Try clearing the search.' : undefined} />
              </td></tr>
            )}
            {filtered.map((r, i) => (
              <tr key={r.id ?? i} className={onRowClick ? 'row-click' : ''}
                onClick={() => onRowClick?.(r)}>
                {selectable && (
                  <td className="th-check" onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={!!selected?.has(r.id!)}
                      onChange={e => {
                        const next = new Set(selected);
                        e.target.checked ? next.add(r.id!) : next.delete(r.id!);
                        onSelect?.(next);
                      }} />
                  </td>
                )}
                {columns.map(c => <td key={c.key}>{c.render ? c.render(r) : String((r as any)[c.key] ?? '')}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {footer}
    </div>
  );
}
